import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { NetworkStatusService } from "../shared/services/network-status.service";

/** Le relais (nginx, ingress) a répondu, pas le serveur : réseau jusqu'au serveur coupé. */
const RELAY_DOWN = new Set([502, 504]);

/**
 * Alimente `NetworkStatusService` à partir de chaque requête vers NOTRE serveur (URL relative ou
 * de la même origine — une API tierce injoignable ne dit rien de notre réseau) :
 *  - réponse (tout code, sauf 502 / 504 du relais) → serveur joignable ;
 *  - échec sans réponse (statut 0) ou 502 / 504 → soupçon, vérifié par une sonde ;
 *  - durée → « réseau lent » au-delà de `NETWORK_SLOW_MS`.
 * N'affiche rien et ne modifie aucune requête. Enregistré en DERNIER (au plus près du réseau) :
 * il voit le statut brut, avant tout traitement d'erreur.
 */
@Injectable()
export class NetworkStatusInterceptor implements HttpInterceptor {
  private network = inject(NetworkStatusService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!isOwnServer(req.url)) return next.handle(req);
    const done = this.network.trackRequest();
    return next.handle(req).pipe(
      tap({
        next: (e) => {
          if (e instanceof HttpResponse) this.network.reportContact();
        },
        error: (e: unknown) => {
          if (e instanceof HttpErrorResponse && (e.status === 0 || RELAY_DOWN.has(e.status))) this.network.reportFailure();
          else if (e instanceof HttpErrorResponse) this.network.reportContact();
        },
      }),
      finalize(done),
    );
  }
}

/** URL relative (`/api/…`) ou de la même origine. */
export function isOwnServer(url: string): boolean {
  if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) return !url.startsWith("//");
  try {
    return typeof location !== "undefined" && new URL(url).origin === location.origin;
  } catch {
    return false;
  }
}
